<template>
  <div
    id="application-tab"
    tabindex="0"
    role="tabpanel"
    aria-labelledby="application"
    class="focus:outline-none"
  >
    <portal to="csvBtn">
      <a class="items-center hidden md:flex" @click="csvExport()" href="#">
        <span class="flex-none">
          <i class="mdi mdi-download"></i>
        </span>
        <span class="ml-2">CSV</span>
      </a>
    </portal>
    <modal
      :id="'breakdown'"
      :title="'Total Program Price Breakdown'"
      @closemodal="resetStudent"
      v-if="showModal"
      price-break(slot="modal-content"
      @close="resetStudent"
    />
    <portal to="searchbox">
      <label class="block text-sm">
        <span class="font-semibold text-gray-600 flex justify-between sr-only">
          <span>Search</span>
        </span>
        <input
          class="form-input min-h-10 block w-full"
          type="text"
          v-model="tableData.search"
          placeholder="Search"
          @input="getStudents(searchUrl)"
        />
      </label>
    </portal>
    <filters
      @getFilterData="getFilterData"
      :showApproval="true"
      :showCity="true"
    />
    <noRecords v-if="!loading & (students.length == 0)" />
    <datatable
      v-else
      :columns="columns"
      :sortKey="sortKey"
      :sortOrders="sortOrders"
      @sort="sortBy"
      role="region"
      aria-label="Application Results"
    >
      <tr v-if="loading">
        <td class="card-data">
          <div class="w-full md:w-columnfixed-md3">Loading Please Wait..</div>
        </td>
        <td class="card-data">
          <div class="w-full md:w-columnfixed-sm" />
        </td>
        <td class="card-data">
          <div class="w-full md:w-columnfixed-sm" />
        </td>
        <td class="card-data">
          <div class="w-full md:w-columnfixed-md3" />
        </td>
        <td class="card-data">
          <div class="w-full md:w-columnfixed-sm" />
        </td>
        <td class="card-data">
          <div class="w-full md:w-columnfixed-sm" />
        </td>
        <td class="card-data">
          <div class="w-full md:w-columnfixed-sm" />
        </td>
        <td class="card-data">
          <div class="w-full md:w-columnfixed-sm" />
        </td>
        <td class="card-data">
          <div class="w-full md:w-columnfixed-md2" />
        </td>
        <td class="card-data">
          <div class="w-full md:w-columnfixed-ctrl" />
        </td>
      </tr>
      <p class="text-red-600" v-else-if="students.length == 0">
        | No records found!
      </p>
      <tbody v-else>
        <tr
          class="card-row"
          v-for="(student, index) in students"
          :key="student.id + 'i' + index"
        >
          <td class="card-section">
            <div class="card-label">
              Student Name and Email
            </div>
            <div class="w-full md:w-columnfixed-md3">
              <span
                class="block text-sm font-semibold text-gray-700"
                @click="
                  setTabData(student.session_id, student.approval_application)
                "
              >
                <router-link
                  class="student-link"
                  :to="
                    '/student-detail' +
                      '/' +
                      student.student_id +
                      '/application'
                  "
                >
                  {{ student.name }}
                </router-link>
              </span>
              <span class="break-all">
                <a :href="'mailto:' + student.email"> {{ student.email }}</a>
              </span>
            </div>
          </td>
          <td class="card-data">
            <div class="card-label">
              Major
            </div>
            <div
              class="xs:text-left xs:w-1/2 sm:w-2/3 md:w-columnfixed-sm break-words"
            >
              {{ student.major || "-" }}
            </div>
          </td>
          <td class="card-data">
            <div class="card-label">
              Academic Standing
            </div>
            <div
              class="xs:text-left xs:w-1/2 sm:w-2/3 md:w-columnfixed-sm break-words"
            >
              {{ student.year || "-" }}
            </div>
          </td>

          <td class="card-section">
            <div class="card-label">
              Program Name
            </div>
            <div class="w-full md:w-columnfixed-md3 break-words">
              <a :href="student.url" v-if="student.url" target="_blank">{{
                student.program_name
              }}</a>
              <span class="block" v-else>{{ student.program_name }}</span>
              <span class="block">{{ student.site }}</span>
            </div>
          </td>
          <td class="card-data">
            <div class="card-label">
              Program Dates
            </div>
            <div class="xs:text-left xs:w-1/2 sm:w-2/3 md:w-columnfixed-sm">
              <datesComponent
                :id="student.id + 'r' + index"
                :dates="student.program_dates"
                :start-is-final="student.start_date_final > 0"
                :end-is-final="student.end_date_final > 0"
              />
            </div>
          </td>
          <td class="card-data">
            <div class="card-label">
              Application Deadline
            </div>
            <div class="xs:text-left xs:w-1/2 sm:w-2/3 md:w-columnfixed-sm">
              {{ student.application_deadline || "-" }}
            </div>
          </td>

          <td class="card-data">
            <div class="card-label">
              | Application Date
            </div>
            <div class="xs:text-left xs:w-1/2 sm:w-2/3 md:w-columnfixed-sm">
              {{ student.applied_date || "-" }}
            </div>
          </td>
          <td class="card-data">
            <div class="card-label">
              | Program Price
            </div>
            <div class="xs:text-left xs:w-1/2 sm:w-2/3 md:w-columnfixed-sm">
              ${{ student.price.toLocaleString("en-US") }}
            </div>
          </td>
          <td class="card-data">
            <div class="card-label">
              Approval Form
            </div>
            <div
              v-if="
                !student.form_rules ||
                  !student.form_rules.includes('University Approval')
              "
            >
              <p>-</p>
            </div>
            <div
              v-else
              class="xs:text-left xs:w-1/2 sm:w-2/3 md:w-columnfixed-md2"
            >
              <span
                class="flex items-center"
                v-if="
                  approvedApplications.includes(student.approval_application)
                "
              >
                <iconF
                  :icon-name="'check-circle'"
                  :classes="'text-success-900 w-4'"
                  :id="student.id + 'r' + index + '-icon'"
                />
                <span class="ml-2 text-sm text-gray-700">Completed</span>
              </span>
              <button
                v-else
                class="approve hover:bg-yellow-900 focus:bg-yellow-900 focus:outline-none"
                @click="approveStudent(student)"
              >
                <span>Approve</span>
              </button>
            </div>
          </td>
          <td class="card-controls">
            <dropdownComponent :row="index + 1" :tabName="'application'">
              <template #dropdown-options>
                <li
                  @click="
                    setTabData(student.session_id, student.approval_application)
                  "
                >
                  <router-link
                    class="dropdown-item"
                    :to="
                      '/student-detail' +
                        '/' +
                        student.student_id +
                        '/application'
                    "
                  >
                    View Detail</router-link
                  >
                </li>
                <li class="border-t.border-gray-200">
                  <a class="dropdown-item" :href="'mailto:' + student.email"
                    >Email Student</a
                  >
                </li>
                <li class="border-t.border-gray-200">
                  <a class="dropdown-item" href="mailto:api-urs@apiabroad.com"
                    >Email API</a
                  >
                </li>
              </template>
            </dropdownComponent>
          </td>
        </tr>
      </tbody>
    </datatable>
    <pagination
      :pagination="pagination"
      @prev="getStudents(pagination.prevPageUrl)"
      @next="getStudents(pagination.nextPageUrl)"
    />
  </div>
</template>

<script>
import datatables from "../DataTables";
import pagination from "../Pagination";
import programDates from "../programDates";
import noRecords from "../NoRecords";
import { tabsMixin } from "../../mixins/tabsMixins";
import dropdownComponent from "../../SiteComponents/my-students/misc/dropdown";
import FeatherComponent from "../../SiteComponents/featherIcon";
import filters from "../filter/Filters.vue";

export default {
  mixins: [tabsMixin],
  components: {
    datatable: datatables,
    pagination: pagination,
    datesComponent: programDates,
    noRecords: noRecords,
    dropdownComponent: dropdownComponent,
    iconF: FeatherComponent,
    filters: filters,
  },
  created() {
    this.getStudents(this.searchUrl, true);
  },
  data() {
    let sortOrders = {};
    //columns headers for this table, mus match a row from columnsWhiteList->row->name in tabMixin.js
    let tabColumns = [
      "nameEmail",
      "major",
      "academicStanding",
      "programName",
      "programDates",
      "applicationDeadline",
      "appliedDate",
      "programPrice",
      "approvalForm",
    ];
    tabColumns.forEach((column) => {
      sortOrders[column] = 0;
    });
    return {
      searchUrl: process.env.VUE_APP_ENROLLMENT_URL + "/api/students",
      id: "",
      loading: true,
      students: [],
      tabColumns: tabColumns,
      sortKey: "col1",
      sortOrders: sortOrders,
      perPage: ["10", "20", "30"],
      tableData: {
        draw: 0,
        length: 25,
        search: "",
        column: 0,
        dir: "asc",
        status: ["Applicant"],
        year: new Date().getFullYear(),
        current_year: new Date().getFullYear(),
        filters: {
          city: "",
          sessions: [],
          majors: [],
          approvalStatus: "",
        },
      },
    };
  },
  methods: {
    setYear(event) {
      this.tableData.year = event.target.value;
      this.getStudents(this.searchUrl);
    },
    approvalComplete(text) {
      const approvalText = text ? text.toLowerCase() : "";
      return approvalText.indexOf("student approved") !== -1;
    },
    prepDataForCsvExport(csvData) {
      return csvData.map((item) => ({
        Name: item.name,
        Email: item.email,
        Major: item.major,
        Academic_standing: item.year,
        Program_Name: item.program_name,
        Location: item.site,
        Program_Dates: item.program_dates,
        Application_deadline: item.application_deadline,
        Program_Price: item.price,
        Approval_form:
          !item.form_rules || !item.form_rules.includes("University Approval")
            ? "-"
            : this.approvedApplications.includes(item.approval_application)
            ? "Completed"
            : "Approve",
      }));
    },
  },
};
</script>

<style lang="postcss" scoped>
.check-approval {
  @apply text-blue-700;
  font-size: 1.75rem;
}
button.approve {
  @apply inline-block text-center rounded font-semibold;
  @apply border-2 border-transparent px-4 min-h-10 bg-yellow-500 text-gray-700;
}
</style>
